import { scopeStateSchema } from '@shared/models/scope-selection.model';
import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { useMemo } from 'react';
import { createLocalStorageStore } from '@client/helpers/local-storage.helpers';
import {
  SCOPE_PRESETS,
  ScopePreset,
  ScopePresetId,
} from '@features/lifecycles/scope-presets.data';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { applyScopeQueryOverride } from '@shared/helpers/scope-query-params.helpers';
import { sanitizeSelectableSbefGroups } from '@shared/helpers/sbef-scope.helpers';
import { Lifecycle } from '@shared/models/lifecycles.model';
import { SbefScopeGroupKey } from '@shared/construction/sbef-custom-groups.data';
import {
  DEFAULT_SCOPE,
  getInitialScopeState,
  SCOPE_STORAGE_KEY,
  ScopeDimensions,
  ScopeState,
} from './scope-migration.helpers';

export type { ScopeDimensions, ScopeState };

/** A scope with its dimensions resolved, plus the preset it came from (if any). */
export type ResolvedScope = ScopeDimensions & { preset?: ScopePreset };

export const overviewScopeStore = createLocalStorageStore<ScopeState>(
  SCOPE_STORAGE_KEY,
  // Only used when `scope` is absent, which is exactly when a legacy scope matters.
  getInitialScopeState(),
);

// Alias for internal use
const overviewScope = overviewScopeStore;

type ProjectScopeContext = {
  id: number;
  scope: ScopeState;
  onChange: (scope: ScopeState) => void;
};
const useProjectScopeContext = createWithEqualityFn<{
  context?: ProjectScopeContext;
}>(() => ({}), shallow);

/** Bind the active project without copying its scope into the overview selection. */
export const bindProjectScope = (
  context: ProjectScopeContext,
): (() => void) => {
  useProjectScopeContext.setState({ context });
  return () => {
    if (
      useProjectScopeContext.getState().context?.onChange === context.onChange
    )
      useProjectScopeContext.setState({ context: undefined });
  };
};

const get = (): ScopeState | undefined =>
  useProjectScopeContext.getState().context?.scope ?? overviewScope.get();

const set = (scope: ScopeState): void => {
  const { context } = useProjectScopeContext.getState();
  if (!context) {
    overviewScope.set(scope);
    return;
  }
  useProjectScopeContext.setState({ context: { ...context, scope } });
  context.onChange(scope);
};

export const presetDimensions = (preset: ScopePreset): ScopeDimensions => ({
  lifecycles: preset.lifecycles,
  sbefGroups: preset.sbefGroups,
  conservative: preset.genericClimateData === 'conservative',
});

/**
 * The one expansion of a stored scope into dimensions. Kept private so no consumer
 * can branch on {@link ScopeState} and derive its own answer. Returned fields are
 * stable references, which the store's shallow equality relies on.
 */
const resolveScope = (state: ScopeState = DEFAULT_SCOPE): ResolvedScope => {
  switch (state.kind) {
    case 'preset': {
      const preset = SCOPE_PRESETS.find(({ id }) => id === state.presetId);
      // A key removed since it was stored resolves to the default scope.
      if (!preset) return resolveScope(DEFAULT_SCOPE);
      return { ...presetDimensions(preset), preset };
    }
    case 'saved':
      return { ...presetDimensions(state.preset), preset: state.preset };
    case 'custom':
      return {
        lifecycles: state.lifecycles,
        sbefGroups: state.sbefGroups,
        conservative: state.conservative,
      };
    default:
      return assertUnreachable(state);
  }
};

/**
 * Persist edits against the stored scope, not a print-URL override.
 * Writing the override back would leak PDF query params into localStorage.
 */
const getStoredScope = (): ResolvedScope =>
  resolveScope(get() ?? DEFAULT_SCOPE);

type LocationScopeCache = {
  state: ScopeState;
  search: string;
  resolved: ResolvedScope;
};

let locationScopeCache: LocationScopeCache | undefined;

/**
 * Apply print-URL scope params if they exist. Cached so Zustand selectors keep
 * stable references across renders (a new object every call would re-render forever).
 */
const resolveScopeWithLocationOverride = (
  state: ScopeState = DEFAULT_SCOPE,
): ResolvedScope => {
  const search = typeof window === 'undefined' ? '' : window.location.search;
  if (
    locationScopeCache &&
    locationScopeCache.state === state &&
    locationScopeCache.search === search
  ) {
    return locationScopeCache.resolved;
  }
  const stored = resolveScope(state);
  const next = search === '' ? stored : applyScopeQueryOverride(stored, search);
  // Query override is a snapshot of dimensions, not a named preset.
  const resolved: ResolvedScope =
    next === stored
      ? stored
      : {
          lifecycles: next.lifecycles,
          sbefGroups: next.sbefGroups,
          conservative: next.conservative,
        };
  locationScopeCache = { state, search, resolved };
  return resolved;
};

const useScopeValue = <R>(selector: (scope: ResolvedScope) => R): R => {
  const context = useProjectScopeContext(({ context }) => context);
  return overviewScope.useStore((overview) =>
    selector(
      resolveScopeWithLocationOverride(
        context?.scope ?? overview ?? DEFAULT_SCOPE,
      ),
    ),
  );
};

/** Replace the scope by hand, dropping any preset the dimensions came from. */
const updateScope = (changes: Partial<ScopeDimensions>): void => {
  const { lifecycles, sbefGroups, conservative } = getStoredScope();
  set({ kind: 'custom', lifecycles, sbefGroups, conservative, ...changes });
};

/** The current scope, resolved, with print-URL query override when present. */
export const useScope = (): ResolvedScope => useScopeValue((scope) => scope);

/** The current scope, resolved, outside React. */
export const getScope = (): ResolvedScope =>
  resolveScopeWithLocationOverride(get() ?? DEFAULT_SCOPE);

/**
 * The scope a preset stands for, without making it the current one — for
 * previewing what picking it would report.
 */
export const getPresetScope = (presetId: ScopePresetId): ResolvedScope =>
  resolveScope({ kind: 'preset', presetId });

/** The preset backing the current scope, or `undefined` when it was set by hand. */
export const useActiveScopePreset = (): ScopePreset | undefined =>
  useScopeValue(({ preset }) => preset);

/** Select an immutable snapshot of an organization scope. */
export const selectSavedScopePreset = (
  preset: ScopePreset,
  organization: string,
): void => set(scopeStateSchema.parse({ kind: 'saved', preset, organization }));

/** Keep dimensions while dropping a name owned by a different organization. */
export const detachForeignSavedScope = (organization: string): void => {
  const state = get();
  if (state?.kind === 'saved' && state.organization !== organization)
    updateScope({});
};

/** Make a preset the current scope. */
export const selectScopePreset = (presetId: ScopePresetId): void =>
  set({ kind: 'preset', presetId });

/**
 * Keep the current dimensions but drop the preset they came from, for edits that
 * contradict the preset without going through a setter here — see
 * {@link useSetCalculationPeriod}. A no-op on an already custom scope.
 */
export const detachScopePreset = (): void => updateScope({});

export const useSelectedLifecycles = (): Lifecycle[] =>
  useScopeValue(({ lifecycles }) => lifecycles);

export const getSelectedLifecycles = (): Lifecycle[] => getScope().lifecycles;

export const setLifecycles = (lifecycles: Lifecycle[]): void =>
  updateScope({ lifecycles });

export const useSelectedSbefGroups = (): SbefScopeGroupKey[] => {
  const sbefGroups = useScopeValue((scope) => scope.sbefGroups);
  return useMemo(() => sanitizeSelectableSbefGroups(sbefGroups), [sbefGroups]);
};

export const getSelectedSbefGroups = (): SbefScopeGroupKey[] =>
  sanitizeSelectableSbefGroups(getScope().sbefGroups);

export const setSelectedSbefGroups = (sbefGroups: SbefScopeGroupKey[]): void =>
  updateScope({ sbefGroups });

export const useConservativeGenericCarbon = (): boolean =>
  useScopeValue(({ conservative }) => conservative);

export const setConservativeGenericCarbon = (conservative: boolean): void =>
  updateScope({ conservative });
